/* Compact homepage embed of the FireBar Labs store locator.
   Loads the same stores-public.json, renders a small map + nearest list. */
(function () {
  'use strict';
  const cfg = window.FBL_LOCATOR_EMBED_CONFIG || {};
  const els = {
    map: document.getElementById('fbl-locator-embed-map'),
    list: document.getElementById('fbl-locator-embed-list'),
    empty: document.getElementById('fbl-locator-embed-empty'),
    search: document.getElementById('fbl-locator-embed-search'),
    geo: document.getElementById('fbl-locator-embed-geo'),
  };
  if (!els.map || !cfg.storesJsonUrl) return;

  let stores = [];
  let map = null;
  let userLoc = null;

  function haversine(lat1, lng1, lat2, lng2) {
    const R = 3958.8;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }
  function escapeHtml(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

  async function loadStores() {
    try {
      const r = await fetch(cfg.storesJsonUrl, {cache: 'default'});
      const data = await r.json();
      return data.filter(s => s.published !== false && s.lat && s.lng);
    } catch (e) {
      console.error('[fbl-embed] failed to load stores', e);
      return [];
    }
  }

  function renderNearest() {
    if (!userLoc) {
      els.empty.style.display = 'block';
      return;
    }
    els.empty.style.display = 'none';
    const ranked = stores.map(s => ({...s, _d: haversine(userLoc.lat, userLoc.lng, s.lat, s.lng)}))
      .sort((a, b) => a._d - b._d).slice(0, 5);
    els.list.innerHTML = '<h3>Closest 5 retailers</h3>' + ranked.map(s => `
      <a class="fbl-locator-embed__item" href="/pages/store-${s.slug}">
        ${s.photo ? `<img src="${escapeHtml(s.photo)}" alt="${escapeHtml(s.name)}" loading="lazy">` : '<div class="fbl-locator-embed__item-photo--empty"></div>'}
        <div class="fbl-locator-embed__item-body">
          <strong>${escapeHtml(s.name)}</strong>
          <span>${escapeHtml(s.city)}, ${escapeHtml(s.state)}</span>
          <span class="fbl-locator-embed__item-dist">${s._d.toFixed(1)} mi away${s.rating ? ` · ★ ${parseFloat(s.rating).toFixed(1)}` : ''}</span>
        </div>
      </a>
    `).join('');
  }

  function fitMap() {
    if (!map) return;
    if (userLoc) {
      // Fit to user + nearest 5
      const ranked = stores.map(s => ({...s, _d: haversine(userLoc.lat, userLoc.lng, s.lat, s.lng)}))
        .sort((a, b) => a._d - b._d).slice(0, 5);
      const bounds = ranked.reduce((b, s) => b.extend([s.lng, s.lat]),
        new mapboxgl.LngLatBounds([userLoc.lng, userLoc.lat], [userLoc.lng, userLoc.lat]));
      map.fitBounds(bounds, {padding: 60, maxZoom: 11, duration: 600});
    }
  }

  function buildFeatures() {
    return {
      type: 'FeatureCollection',
      features: stores.map(s => ({
        type: 'Feature',
        geometry: {type: 'Point', coordinates: [s.lng, s.lat]},
        properties: {slug: s.slug, name: s.name},
      })),
    };
  }

  function initMap() {
    if (!cfg.mapboxToken || !window.mapboxgl) {
      console.warn('[fbl-embed] initMap aborted — token or library missing');
      return;
    }
    try {
      mapboxgl.accessToken = cfg.mapboxToken;
      map = new mapboxgl.Map({
        container: 'fbl-locator-embed-map',
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-98.35, 39.5],
        zoom: 3.4,
        attributionControl: true,
        cooperativeGestures: true,
      });
      window.__fblEmbedMap = map;
      map.on('error', (e) => console.error('[fbl-embed] map error', e));
      map.on('load', () => {
        // Add empty source first; we'll setData below once stores have loaded.
        map.addSource('stores', {
          type: 'geojson',
          cluster: true,
          clusterMaxZoom: 12,
          clusterRadius: 50,
          data: buildFeatures(),
        });
        // Always re-set the data after a tick to handle any race with the
        // async stores load — defensive belt-and-suspenders.
        setTimeout(() => {
          if (map.getSource('stores')) {
            map.getSource('stores').setData(buildFeatures());
          }
        }, 50);
        map.addLayer({id: 'cl', type: 'circle', source: 'stores', filter: ['has', 'point_count'],
          paint: {'circle-color': cfg.pinColor || '#5da90a', 'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30], 'circle-stroke-width': 3, 'circle-stroke-color': '#fff'}});
        map.addLayer({id: 'cnt', type: 'symbol', source: 'stores', filter: ['has', 'point_count'],
          layout: {'text-field': '{point_count_abbreviated}', 'text-size': 12, 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold']},
          paint: {'text-color': '#fff'}});
        map.addLayer({id: 'pts', type: 'circle', source: 'stores', filter: ['!', ['has', 'point_count']],
          paint: {'circle-color': cfg.pinColor || '#5da90a', 'circle-radius': 6, 'circle-stroke-width': 2, 'circle-stroke-color': '#fff'}});
        // Single-store click → popup with link to per-store page
        map.on('click', 'pts', (e) => {
          const f = e.features[0];
          new mapboxgl.Popup().setLngLat(f.geometry.coordinates)
            .setHTML(`<strong>${f.properties.name}</strong><br><a href="/pages/store-${f.properties.slug}">View →</a>`).addTo(map);
        });
        // Cluster click → zoom into the cluster
        map.on('click', 'cl', (e) => {
          const features = map.queryRenderedFeatures(e.point, {layers: ['cl']});
          if (!features.length) return;
          const clusterId = features[0].properties.cluster_id;
          map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.easeTo({center: features[0].geometry.coordinates, zoom});
          });
        });
        map.on('mouseenter', 'cl', () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'cl', () => { map.getCanvas().style.cursor = ''; });
        map.on('mouseenter', 'pts', () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'pts', () => { map.getCanvas().style.cursor = ''; });
        requestAnimationFrame(() => map.resize());
        setTimeout(() => map.resize(), 250);
        setTimeout(() => map.resize(), 1000);
        fitMap();
      });
      if (window.ResizeObserver) {
        new ResizeObserver(() => map && map.resize()).observe(els.map);
      }
    } catch (e) {
      console.error('[fbl-embed] map init failed', e);
    }
  }

  function useGeo() {
    if (!navigator.geolocation) return;
    els.geo.disabled = true;
    navigator.geolocation.getCurrentPosition(
      pos => {
        userLoc = {lat: pos.coords.latitude, lng: pos.coords.longitude};
        renderNearest();
        fitMap();
        els.geo.disabled = false;
      },
      () => { els.geo.disabled = false; },
      {timeout: 8000, maximumAge: 60000}
    );
  }

  let searchTimer = null;
  function onSearch(q) {
    clearTimeout(searchTimer);
    if (!q || q.length < 3) return;
    searchTimer = setTimeout(() => {
      if (!cfg.mapboxToken) return;
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${cfg.mapboxToken}&country=us,pr,vi&limit=1&types=postcode,place,locality,address`;
      fetch(url).then(r => r.json()).then(data => {
        if (!data.features || !data.features.length) return;
        const [lng, lat] = data.features[0].center;
        userLoc = {lat, lng};
        renderNearest();
        fitMap();
      }).catch(() => {});
    }, 400);
  }

  els.search?.addEventListener('input', e => onSearch(e.target.value));
  els.search?.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); onSearch(e.target.value); } });
  els.geo?.addEventListener('click', useGeo);

  async function start() {
    stores = await loadStores();
    if (!stores.length) return;
    if (window.mapboxgl) initMap();
    else {
      const wait = () => window.mapboxgl ? initMap() : setTimeout(wait, 80);
      wait();
    }
  }
  start();
})();
